import * as _ from "lodash";
import numeral from "numeral";

import { ExtensionBase } from "../../extensionBase";
import { UiUtils } from "../../../common/uiUtils";
import { viewerOptions } from "../../../viewer";
import * as SmartDrawings from "../../../smartDrawings";
import { ObjectRecognitionData, INode, BalconyInfo, SavedClasses, RecognitionData, propertyInfo, BalconyEvents } from "./balconyInfo";
import { CustomColorExtension } from "../../customColor/customColorExtension";
import { ModelApi } from "../../../api/modelApi";
import { ViewerUtils } from "../../../common/viewerUtils";
import { GlassPanel } from "./constructGlassesPanel";

export class BalconyGlassExtension extends ExtensionBase {
    static extensionName = "BalconyGlassExtension";
    private static subToolbarName = "SmartDrawings.BalconyExtensions";

    private saveFileName = "balconyInfo.json";
    private static autoSaveTimeout = 10000; // 10s.
    private static localStorageItemName = "SmartDrawings.Balcony.Data";

    private timeout: NodeJS.Timeout;

    private subToolbar: Autodesk.Viewing.UI.ControlGroup;

    private constructGlassedButton: Autodesk.Viewing.UI.Button;
    private glassPanel: GlassPanel;

    private searchBalconyButton: Autodesk.Viewing.UI.Button;
    private deleteFindGroupObjectsButton: Autodesk.Viewing.UI.Button;
    private selectAllSingleGlassesButton: Autodesk.Viewing.UI.Button;
    private balconyGlassButton: Autodesk.Viewing.UI.Button;
    private classificationButton: Autodesk.Viewing.UI.Button;
    private downloadButton: Autodesk.Viewing.UI.Button;
    private saveButton: Autodesk.Viewing.UI.Button;

    private balconyInfo: BalconyInfo;

    private propertyDefinitions: { [key: string]: SmartDrawings.PropertyDefinition; } = {};

    private modelNumber = -1;

    private selectedBalconyId = null;

    private recognitionData: RecognitionData;

    private searchParams: ObjectRecognitionData[] = [];

    constructor(viewer: Autodesk.Viewing.GuiViewer3D, options: any) {
        super(viewer, options);

        this.balconyInfo = new BalconyInfo(this.viewer);

        this.glassPanel = new GlassPanel(this.viewer, this.balconyInfo, this.viewer.container, "SmartDrawings-Panel-Glass");
    }

    static registerExtension() {
        super.registerExtension(BalconyGlassExtension.extensionName, BalconyGlassExtension);
    }

    private setSubtoolbarState(id: string, state: boolean) {
        let toolbar = this.getControlGroup(BalconyGlassExtension.subToolbarName);
        let controls = toolbar.getNumberOfControls();
        for (let i = 0; i < controls; i++) {
            let control = toolbar.getControl(toolbar.getControlId(i));
            if (control instanceof Autodesk.Viewing.UI.Button)
                UiUtils.setButtonEnabled(control, state);
        }
    }

    load(): boolean {
        console.log(`${BalconyGlassExtension.extensionName} is loaded!`);

        return super.load();
    };

    unload(): boolean {
        if (this.checkUda()) {
            window.removeEventListener("beforeunload", this.onBeforeUnload.bind(this));
            this.viewer.removeEventListener(BalconyEvents.SD_BALCONY_DATA_UPDATED, this.onDataUpdated.bind(this));
        }

        return super.unload();
    };

    async onToolbarCreated() {
        await this.createUi();
    }

    private async createUi() {
        if (this.checkUda()) {
            this.subToolbar = this.getControlGroup(BalconyGlassExtension.subToolbarName);

            this.constructGlassedButton = UiUtils.createButton({
                id: "SmartDrawings.ConstructGlassedButton",
                tooltip: "Construct glasses",
                iconClass: "ion-md-construct",
                onClick: async () => {
                    //this.glassPanel = new GlassPanel(this.viewer, this.balconyInfo, this.viewer.container, "SmartDrawings-Panel-Glass");

                    this.glassPanel.setVisible(!this.glassPanel.isVisible(), this.selectedBalconyId);
                }
            });

            this.subToolbar.addControl(this.constructGlassedButton);

            this.searchBalconyButton = UiUtils.createButton({
                id: "SmartDrawings.FindGroupObjectsButton",
                tooltip: "Search balconies",
                iconClass: "ion-md-add-circle-outline",
                onClick: async () => {
                    //await this.findGlassRowObjects();
                    await this.findBalconyObjects();
                }
            });

            //this.singleGlassButton.setState(Autodesk.Viewing.UI.Button.State.DISABLED);

            this.subToolbar.addControl(this.searchBalconyButton);

            this.deleteFindGroupObjectsButton = UiUtils.createButton({
                id: "SmartDrawings.DeleteObjectsButton",
                tooltip: "Delete objects",
                iconClass: "ion-md-close",
                onClick: async () => {
                    await this.deleteSelectedNodes();
                }
            });

            //this.singleGlassButton.setState(Autodesk.Viewing.UI.Button.State.DISABLED);

            this.subToolbar.addControl(this.deleteFindGroupObjectsButton);

            this.selectAllSingleGlassesButton = UiUtils.createButton({
                id: "SmartDrawings.SelectAllObjectsButton",
                tooltip: "Select all objects",
                iconClass: "ion-md-checkbox-outline",
                onClick: async () => {
                    await this.selectAllNodes();
                }
            });

            this.subToolbar.addControl(this.selectAllSingleGlassesButton);

            //this.balconyGlassButton = UiUtils.createButton({
            //    id: "SmartDrawings.BalconyGlassButton",
            //    tooltip: "Combine single glass objects",
            //    iconClass: "ion-md-link",
            //    onClick: async () => {
            //        await this.makeBalconyGroups();
            //    }
            //});

            //this.subToolbar.addControl(this.balconyGlassButton);

            this.classificationButton = UiUtils.createButton({
                id: "SmartDrawings.ClassificationButton",
                tooltip: "Classify groups",
                iconClass: "ion-md-analytics",
                onClick: async () => {
                    await this.classifyGroups();
                }
            });

            this.subToolbar.addControl(this.classificationButton);

            this.downloadButton = UiUtils.createButton({
                id: "SmartDrawings.ReportButton",
                tooltip: "Download report",
                iconClass: "ion-md-download",
                onClick: async () => {
                    await this.downloadReport();
                }
            });

            this.subToolbar.addControl(this.downloadButton);

            this.saveButton = UiUtils.createButton({
                id: "SmartDrawings.SaveButton",
                tooltip: "Save data",
                iconClass: "ion-md-save",
                onClick: async () => {
                    await this.saveInfo();
                }
            });

            this.subToolbar.addControl(this.saveButton);

            this.setSubtoolbarState(BalconyGlassExtension.subToolbarName, false);

            this.viewer.addEventListener(Autodesk.Viewing.OBJECT_TREE_CREATED_EVENT,
                this.onTreeLoaded.bind(this));

            // Leave warning
            window.addEventListener("beforeunload", this.onBeforeUnload.bind(this));

            this.viewer.addEventListener(BalconyEvents.SD_BALCONY_DATA_UPDATED, this.onDataUpdated.bind(this));
        }
    }

    private async onBeforeUnload(event) {
        // If not autosaved yet, do it now
        if (this.balconyInfo.isUpdated())
            await this.onBalconyDataUpdated();

        if (this.getLocalData(viewerOptions.guid)) {
            event.preventDefault();
            event.returnValue = true;
        }

        delete event["returnValue"];
    }

    private onDataUpdated({ updated }) {
        clearTimeout(this.timeout);
        if (updated)
            this.timeout = setTimeout((this.onBalconyDataUpdated.bind(this)), BalconyGlassExtension.autoSaveTimeout);
    }

    private checkUda(): boolean {
        const { userDefinedAttributes } = viewerOptions;
        if (userDefinedAttributes == null)
            return false;

        if (userDefinedAttributes["Lumon"]) {
            return true;
        }

        return false;
    }

    async onTreeLoaded() {
        this.setupSelectedNodeElementHighlight();
        await this.initSavedData();

        if (await this.initRecognitionData()) {
            if (await this.initPropertyInfo(this.recognitionData.propertyInfoFilename)) {
                this.setSubtoolbarState(BalconyGlassExtension.subToolbarName, true);
                UiUtils.setButtonEnabled(this.searchBalconyButton, this.searchParams.length > 0);
            }
        }

        this.viewer.removeEventListener(Autodesk.Viewing.OBJECT_TREE_CREATED_EVENT,
            this.onTreeLoaded.bind(this));
    }

    private async onBalconyDataUpdated() {
        let balconyData = this.balconyInfo.getDataAsObject();
        //console.log(json);

        let obj = {
            id: viewerOptions.guid,
            timestamp: new Date(Date.now()).toISOString(),
            data: balconyData
        };

        let json = JSON.stringify(obj, null, 2);

        this.saveLocalData(obj.id, json);
        this.balconyInfo.setUpdated(false);

        UiUtils.notifySuccess("Autosaved locally");
    }

    private getLocalData(identifier: string): string {
        return localStorage.getItem(this.getLocalStorageItemKey(identifier));
    }

    private saveLocalData(identifier: string, data: string) {
        const localStorageItemName = this.getLocalStorageItemKey(identifier);

        try {
            localStorage.setItem(localStorageItemName, data);
        } catch (e) {
            console.log(e);
        }
    }

    private clearLocalData(identifier: string) {
        localStorage.removeItem(this.getLocalStorageItemKey(identifier));
    }

    private getLocalStorageItemKey(identifier: string) {
        return `${BalconyGlassExtension.localStorageItemName}.${identifier}`;
    }

    private async initSavedData() {
        let data = null;

        // We assume data in local storage data is newer than in cloud
        // TODO: Check timestamps ???

        let localData = this.getLocalData(viewerOptions.guid);
        if (localData) {
            let obj = JSON.parse(localData);
            data = obj.data;
        }
        else {
            // Try to get saved balcony data
            try {
                const result = await ModelApi.getJsonData(viewerOptions.guid, this.saveFileName);
                if (!result) {
                    console.log(result);
                    return;
                }

                data = result.data;

            } catch (e) {
                if (e.response.status !== 404)
                    console.log(e);
            }
        }

        if (data) {
            await this.balconyInfo.addGroups(data.groups);
            this.modelNumber = await this.balconyInfo.drawGroups();
            await this.balconyInfo.addClasses(data.classes);

            await this.initNodeInfoToExternalData(this.balconyInfo.groups);

            this.balconyInfo.setUpdated(false);
        }
    }

    private async initRecognitionData(): Promise<boolean> {
        let success = false;
        try {
            const result = await ModelApi.getJsonData(viewerOptions.guid, "recognition.json");
            if (!result)
                UiUtils.notifyWarning("Fetching object recognition data failed");
            else {
                let dataJson = result.data;
                this.recognitionData = await this.checkModelSource(dataJson);
                if (this.recognitionData) {
                    this.searchParams = this.recognitionData.objectRecognition;
                    success = true;
                }
                else
                    UiUtils.notifyWarning("Cannot recognize model source");
            }
        } catch (e) {
            if (e.response && e.response.status !== 404)
                console.log(e);
        }

        return success;
    }

    private async initPropertyInfo(fileName: string): Promise<boolean> {
        let success = false;
        try {
            const result = await ModelApi.getJsonData(viewerOptions.guid, fileName);
            if (!result)
                UiUtils.notifyWarning("Fetching property info data failed");
            else {
                propertyInfo.push(...result.data);
                
                this.balconyInfo.propertyInfo = result.data;

                for (let info of this.balconyInfo.propertyInfo) {
                    this.propertyDefinitions[info.propertyDefinition.name] = info.propertyDefinition;
                }

                success = true;
            }
        } catch (e) {
            if (e.response.status !== 404)
                console.log(e);
        }

        return success;
    }

    private async checkModelSource(data: RecognitionData[]): Promise<RecognitionData> {
        let modelSource: string = null;

        // Try to find model source value given when the model was published
        const { userDefinedAttributes } = viewerOptions;
        if (userDefinedAttributes !== null && userDefinedAttributes["modelSource"]) {
            modelSource = userDefinedAttributes["modelSource"];

            let recognition = data.find(r => r.id.toUpperCase() === modelSource.toUpperCase());
            if (recognition)
                return recognition;
        }

        for (let item of data) {
            let dbIds = await (this.getPrimaryModel() as any).findProperty(item.sourceRecognition.property) as Array<number>;
            dbIds = dbIds.sort((n1, n2) => n1 - n2);
            for (let i = 0; i < dbIds.length; i++) {
                if (dbIds[i]) {
                    var props = await this.getPropertiesFromModel(dbIds[i]);
                    if (this.checkSearchProperty(item.sourceRecognition.typeCategory, item.sourceRecognition.typeProperty,
                        item.sourceRecognition.typeValue, item.sourceRecognition.category, item.sourceRecognition.property,
                        item.sourceRecognition.value, item.sourceRecognition.exactMatch, props)) {
                        return item;
                    }
                }
            }
        }

        return null;
    }

    private async findBalconyObjects() {
        this.setSubtoolbarState(BalconyGlassExtension.subToolbarName, false);
        this.viewer.clearSelection();
        this.balconyInfo.reset();

        // Empty nodes property values, Lumon models can't include external data for nodes
        if (viewerOptions.externalData) {
            let { externalData } = viewerOptions as SmartDrawings.Model;
            if (externalData.nodes)
                externalData.nodes.propertyValues = {};
        }

        let propNames: string[] = [];
        for (let prop of this.recognitionData.objectRecognition) {
            propNames.push(prop.typeProperty, prop.property);
        }

        for (let prop of this.balconyInfo.propertyInfo) {
            propNames.push(prop.property);
        }

        const instanceTree = this.getPrimaryModel().getInstanceTree();
        const dbIds = Object.keys(instanceTree.nodeAccess.dbIdToIndex).map(id => Number(id));

        this.getPrimaryModel().getBulkProperties(dbIds, { propFilter: propNames },
            async (allProps) => {
                try {
                    for (let search of this.searchParams) {
                        for (let i = 0; i < allProps.length; i++) {
                            var props = allProps[i].properties;
                            if (this.checkSearchProperty(search.typeCategory, search.typeProperty,
                                search.typeValue, search.category, search.property, search.value,
                                search.exactMatch, props)) {
                                let newGroup = this.balconyInfo.addGroupWithConnectingIds([allProps[i].dbId], props);
                                //await this.addNodeInfoToExternalData(newGroup);
                            }
                        }
                    }

                    if (this.balconyInfo.groups.length > 0) {
                        this.modelNumber = await this.balconyInfo.drawGroups();
                        for (const group of this.balconyInfo.groups) {
                            await this.addNodeInfoToExternalData(group);
                        }
                    }

                    UiUtils.notifySuccess(`Searching balconies completed, found ${this.balconyInfo.groups.length}.`);
                    this.setSubtoolbarState(BalconyGlassExtension.subToolbarName, true);
                } catch (e) {
                    console.log(e);
                    UiUtils.notifyError("Searching balconies failed");
                    this.setSubtoolbarState(BalconyGlassExtension.subToolbarName, true);
                }
            },
            (error) => {
                console.log(error);
                UiUtils.notifyError("Searching balconies failed");
                this.setSubtoolbarState(BalconyGlassExtension.subToolbarName, true);
            });
    }

    private checkSearchProperty(typeCategory: string, typeProperty: string, typeValue: string,
        category: string, property: string, value: string, exactMatch: boolean, props: Autodesk.Viewing.Property[]): boolean {
        if (_.find(props, prop => prop.displayCategory === typeCategory && prop.displayName === typeProperty && prop.displayValue === typeValue)) {
            var prop = _.find(props, prop => prop.displayCategory === category
                && prop.displayName === property
                && (exactMatch ? prop.displayValue === value : _.includes(prop.displayValue.toString(), value)));
            if (prop)
                return true;
        }
        
        return false;
    }

    private async addNodeInfoToExternalData(node: INode) {
        if (!viewerOptions.externalData) {
            let nodeData: SmartDrawings.NodeData = { propertyDefinitions: this.propertyDefinitions, propertyValues: {} };
            let data: SmartDrawings.ExternalData = { propertyDefinitions: {}, propertyValues: {}, nodes: nodeData };
            viewerOptions.externalData = data;
        }

        let { externalData } = viewerOptions as SmartDrawings.Model;

        externalData.nodes.propertyValues[node.id] = node.propertyValues;
    }

    private async initNodeInfoToExternalData(nodes: INode[]) {
        if (!viewerOptions.externalData) {
            let nodeData: SmartDrawings.NodeData = { propertyDefinitions: this.propertyDefinitions, propertyValues: {} };
            let data: SmartDrawings.ExternalData = { propertyDefinitions: {}, propertyValues: {}, nodes: nodeData };
            viewerOptions.externalData = data;
        }

        let { externalData } = viewerOptions as SmartDrawings.Model;

        for (let node of nodes) {
            externalData.nodes.propertyValues[node.id] = node.propertyValues;
        }
    }

    private async deleteSelectedNodes() {
        const selected = this.viewer.getAggregateSelection();
        // Only added entities count
        if (selected.length > 0) {
            const selection = _.find(selected, sel => sel.model.id === this.modelNumber)?.selection;
            if (selection) {
                let model = this.getPrimaryModel();
                for (let node of selection) {
                    this.viewer.toggleSelect(node, this.getAddedModel(), Autodesk.Viewing.SelectionMode.LEAF_OBJECT);
                    let ids = this.balconyInfo.getNodesConnectingIds(node);
                    for (let dbId of ids) {
                        model.setThemingColor(dbId, null, true);
                    }

                    if (this.glassPanel.isVisible())
                        this.glassPanel.removeSelectedBalcony();
                    else
                        this.balconyInfo.removeNode(node);
                }

                this.viewer.impl.invalidate(true);
            }
        }
    }

    private async selectAllNodes() {
        let nodes = await this.balconyInfo.getAllNodes();
        if (nodes.length > 0) {
            this.viewer.select(_.map(nodes, node => node.id),
                this.getAddedModel(),
                Autodesk.Viewing.SelectionMode.REGULAR);
        }
    }

    private async classifyGroups() {
        await this.balconyInfo.classifyGroups();
        UiUtils.notifySuccess(`Classifying balconies completed, found ${this.balconyInfo.classes.length}.`);
    }

    private async downloadReport() {
        const info = await this.balconyInfo.createReportInfo();

        // Note-property is a special case
        let indexOfNote = info[0].indexOf("Note");
        if (indexOfNote >= 0) {
            for (var i = 1; i < info.length; i++) {
                if (info[i].length > 0 && info[i][indexOfNote]) {
                    let note = info[i][indexOfNote].trim();
                    if (note.length > 0) {
                        // Remove new line characters
                        note = note.replace(/(\r\n|\n|\r)/gm, " ");
                        info[i][indexOfNote] = note;
                    }
                }
            }
        }

        let csvContent = "data:text/csv;charset=utf-8,"
            + info.map(e => e.join(";")).join("\n");

        var encodedUri = encodeURI(csvContent);

        // Add link
        var link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "report.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    private async saveInfo() {
        let obj = this.balconyInfo.getDataAsObject();

        //var json = JSON.stringify(obj, null, 2);
        //console.log(json);

        const result = await ModelApi.saveJsonData(viewerOptions.guid, this.saveFileName, obj);
        if (!result)
            UiUtils.notifyWarning("Saving data failed");
        else {
            this.clearLocalData(viewerOptions.guid);
            this.balconyInfo.setUpdated(false);

            UiUtils.notifySuccess("Balcony data saved");
        }
    }

    private setupSelectedNodeElementHighlight() {
        this.viewer.addEventListener(Autodesk.Viewing.AGGREGATE_SELECTION_CHANGED_EVENT, ({ selections }) => {
            this.selectedBalconyId = null;

            // If custom colors are active, don't mess them up.
            if (this.customColorsDisplayed()) {
                return;
            }

            if (!this.modelNumber || this.modelNumber < 0) {
                let num = this.balconyInfo.getInfoNodeModelNumber();
                if (num)
                    this.modelNumber = num;
            }

            var models = this.viewer.getAllModels();
            for (const model of models) {
                this.viewer.clearThemingColors(model);
            }

            const nodeSel = _.find(selections, sel => sel.model.id === this.modelNumber);
            if (nodeSel != null) {
                for (let dbId of nodeSel.dbIdArray) {
                    this.balconyHighlight(models, dbId);

                    this.selectedBalconyId = dbId;
                }

                this.viewer.impl.invalidate(true);
            }
        });
    }

    private balconyHighlight(models: Autodesk.Viewing.Model[], id: number) {
        var modelNum = 0;
        const group = this.balconyInfo.getGroup(id);
        const elementDbIds = group.connectingIds;
        if (group.manual && group.panels.length > 0)
            modelNum = group.panels[0].modelId - 1;
        else
            modelNum = 0;

        for (let ed of elementDbIds) {
            models[modelNum].setThemingColor(ed, new THREE.Vector4(255, 0, 0, 255).toRgba(), true);
        }
    }

    private customColorsDisplayed() {
        const colorExtension = this.viewer.getExtension(CustomColorExtension.extensionName) as CustomColorExtension;
        return colorExtension != null && colorExtension.isActive();
    }

    private getPrimaryModel() {
        return this.viewer.getVisibleModels()[0];
    }

    private getAddedModel() {
        return ViewerUtils.getModel(this.modelNumber, this.viewer);
    }

    private getPropertiesFromModel = (nodeId): Promise<Autodesk.Viewing.Property[]> => {
        return new Promise((resolve, reject) => {
            this.viewer.model.getProperties(
                nodeId,
                result => resolve(result.properties),
                error => reject(error));
        });
    }
}